import React, { useEffect, useState, useCallback, useMemo } from 'react';
import NavHeader from '../components/NavHeader';
import {
  fetchVendors,
  fetchVendor,
  createVendor,
  updateVendor,
  deleteVendor,
  fetchVendorAliases,
  createVendorAlias,
  deleteVendorAlias,
} from '../api';

const EMPTY_FORM = {
  name: '',
  country: '',
  default_currency: '',
  default_gl_account: '',
  bank_info_on_file: false,
};

function VendorsAdmin() {
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Modal state — null = closed, 'add' = new vendor, UUID = editing that vendor
  const [modalMode, setModalMode] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Vendor detail data (projects + aliases)
  const [vendorProjects, setVendorProjects] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [newAliasText, setNewAliasText] = useState('');

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadVendors = useCallback(async () => {
    try {
      const res = await fetchVendors();
      const data = res.data;
      setVendors(Array.isArray(data) ? data : (data?.results ?? []));
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const filteredVendors = useMemo(() =>
    vendors
      .filter(v => v.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [vendors, search]
  );

  const openAdd = () => {
    setFormData({ ...EMPTY_FORM });
    setVendorProjects([]);
    setAliases([]);
    setNewAliasText('');
    setModalMode('add');
  };

  const openEdit = async (vendor) => {
    setFormData({
      name: vendor.name || '',
      country: vendor.country || '',
      default_currency: vendor.default_currency || '',
      default_gl_account: vendor.default_gl_account || '',
      bank_info_on_file: vendor.bank_info_on_file || false,
    });
    setNewAliasText('');
    setModalMode(vendor.id);

    // Load full vendor details (projects) and aliases
    try {
      const [vendorRes, aliasesRes] = await Promise.all([
        fetchVendor(vendor.id),
        fetchVendorAliases(vendor.id),
      ]);
      setVendorProjects(vendorRes.data?.projects || []);
      setAliases(Array.isArray(aliasesRes.data) ? aliasesRes.data : []);
    } catch (err) {
      showToast('Failed to load vendor details');
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setFormData({ ...EMPTY_FORM });
    setVendorProjects([]);
    setAliases([]);
    setNewAliasText('');
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        await createVendor(formData.name);
        showToast('Vendor created successfully', 'success');
      } else {
        const payload = { ...formData };
        if (!payload.country) payload.country = null;
        if (!payload.default_currency) payload.default_currency = null;
        if (!payload.default_gl_account) payload.default_gl_account = null;
        await updateVendor(modalMode, payload);
        showToast('Vendor updated successfully', 'success');
      }
      closeModal();
      loadVendors();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, vendorId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await deleteVendor(vendorId);
      showToast('Vendor deleted', 'success');
      loadVendors();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  // --- Alias handlers ---
  const loadAliases = async (vendorId) => {
    try {
      const res = await fetchVendorAliases(vendorId);
      setAliases(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load aliases', err);
    }
  };

  const handleAddAlias = async () => {
    const text = newAliasText.trim();
    if (!text || modalMode === 'add') return;
    try {
      await createVendorAlias(modalMode, text);
      setNewAliasText('');
      showToast('Alias added', 'success');
      loadAliases(modalMode);
    } catch (err) {
      if (err.response?.status === 409) {
        showToast('This alias is already in use by another vendor', 'error');
      } else {
        showToast('Failed to add alias', 'error');
      }
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    try {
      await deleteVendorAlias(modalMode, aliasId);
      showToast('Alias removed', 'success');
      loadAliases(modalMode);
    } catch (err) {
      showToast('Failed to remove alias', 'error');
    }
  };

  return (
    <div className="projects-page">
      <NavHeader actions={
        <button className="btn btn-primary" onClick={openAdd}>Add Vendor</button>
      } />

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Search */}
      <div className="projects-search">
        <input
          type="text"
          placeholder="Search vendors by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        {search && (
          <span className="search-count">{filteredVendors.length} of {vendors.length} vendors</span>
        )}
      </div>

      {/* Vendors Table */}
      <div className="projects-table-container">
        {loading ? (
          <div className="loading-spinner">Loading vendors...</div>
        ) : (
          <>
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Country</th>
                  <th>Currency</th>
                  <th>GL Account</th>
                  <th>Bank Info</th>
                  <th>Aliases</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((v) => (
                  <tr
                    key={v.id}
                    className="clickable-row"
                    onClick={() => openEdit(v)}
                  >
                    <td>{v.name}</td>
                    <td>{v.country || '\u2014'}</td>
                    <td>{v.default_currency || '\u2014'}</td>
                    <td>{v.default_gl_account || '\u2014'}</td>
                    <td>{v.bank_info_on_file ? 'Yes' : 'No'}</td>
                    <td>{v.alias_count ?? (v.aliases?.length ?? '\u2014')}</td>
                    <td className="project-actions">
                      <button
                        className="btn btn-reject btn-sm"
                        onClick={(e) => handleDelete(e, v.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredVendors.length === 0 && search && (
              <div className="projects-empty">No matching vendors.</div>
            )}
            {vendors.length === 0 && !search && (
              <div className="projects-empty">No vendors yet. Click "Add Vendor" above.</div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {modalMode !== null && (
        <div className="project-modal-overlay" onClick={closeModal}>
          <div className="project-modal project-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="project-modal-header">
              <h2>{modalMode === 'add' ? 'New Vendor' : 'Edit Vendor'}</h2>
              <button className="project-modal-close" onClick={closeModal}>&times;</button>
            </div>

            <div className="project-modal-body">
              {/* Section 1: Vendor Details */}
              <div className="project-modal-row">
                <label>Vendor Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Vendor name"
                  autoFocus
                />
              </div>

              <div className="project-modal-row-group">
                <div className="project-modal-row">
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setField('country', e.target.value)}
                    placeholder="e.g. US, LU, DE"
                  />
                </div>
                <div className="project-modal-row">
                  <label>Default Currency</label>
                  <input
                    type="text"
                    value={formData.default_currency}
                    onChange={(e) => setField('default_currency', e.target.value)}
                    placeholder="e.g. USD, EUR"
                    maxLength={3}
                  />
                </div>
                <div className="project-modal-row">
                  <label>Default GL Account</label>
                  <input
                    type="text"
                    value={formData.default_gl_account}
                    onChange={(e) => setField('default_gl_account', e.target.value)}
                    placeholder="e.g. 600100"
                  />
                </div>
              </div>

              <div className="project-modal-row">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.bank_info_on_file}
                    onChange={(e) => setField('bank_info_on_file', e.target.checked)}
                  />
                  Bank Info on File
                </label>
              </div>

              {/* Section 2: Aliases (edit mode only) */}
              {modalMode !== 'add' && (
                <div className="modal-mappings-section">
                  <div className="modal-mappings-header">
                    <h3>Aliases</h3>
                  </div>
                  <div className="keyword-chips">
                    {aliases.map((alias) => (
                      <span key={alias.id} className="keyword-chip">
                        <span className="keyword-chip-text">{alias.alias_name}</span>
                        <button
                          className="keyword-delete-btn"
                          onClick={() => handleDeleteAlias(alias.id)}
                          title="Remove alias"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    {aliases.length === 0 && (
                      <span style={{ fontSize: '0.85em', color: '#999' }}>No aliases yet</span>
                    )}
                  </div>
                  <div className="keyword-add-row">
                    <input
                      type="text"
                      className="instruction-text-input"
                      placeholder="Add alias name..."
                      value={newAliasText}
                      onChange={(e) => setNewAliasText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newAliasText.trim()) {
                          handleAddAlias();
                        }
                      }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleAddAlias}
                      disabled={!newAliasText.trim()}
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Section 3: Associated Projects (edit mode only) */}
              {modalMode !== 'add' && vendorProjects.length > 0 && (
                <div className="modal-mappings-section">
                  <div className="modal-mappings-header">
                    <h3>Associated Projects</h3>
                  </div>
                  <table className="modal-mappings-table">
                    <thead>
                      <tr>
                        <th>Project Name</th>
                        <th>Status</th>
                        <th>GL Account</th>
                        <th>GL Cost Center</th>
                        <th>Source</th>
                        <th>Times Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorProjects.map((p, idx) => (
                        <tr key={p.project_id || idx}>
                          <td>{p.project_name}</td>
                          <td>
                            <span className={`status-pill status-${p.status || 'active'}`}>
                              {(p.status || 'active') === 'active' ? 'Active' : 'Closed'}
                            </span>
                          </td>
                          <td>{p.gl_account || '\u2014'}</td>
                          <td>{p.gl_cost_center || '\u2014'}</td>
                          <td>
                            {p.source ? (
                              <span className={`source-badge source-${p.source}`}>{p.source}</span>
                            ) : '\u2014'}
                          </td>
                          <td>{p.times_used ?? '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {modalMode !== 'add' && vendorProjects.length === 0 && (
                <div className="modal-mappings-section">
                  <div className="modal-mappings-header">
                    <h3>Associated Projects</h3>
                  </div>
                  <p className="modal-mappings-note">No project mappings for this vendor.</p>
                </div>
              )}
            </div>

            <div className="project-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.name || saving}
              >
                {saving ? 'Saving...' : (modalMode === 'add' ? 'Create Vendor' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorsAdmin;
